/**
 * Analytics Service for handling teacher analytics API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured analytics service
 * @param {Object} config - Configuration options
 * @param {Function} config.onError - Global error handler
 * @returns {Object} The analytics service API
 */
export function createAnalyticsService(config = {}) {
  // Get configuration functions
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Analytics service', error));

  /**
   * Get class diagnostic report for a specific course
   * @param {string} courseId - The course ID
   * @returns {Promise<Object>} The diagnostic report data
   */
  const getClassDiagnosticReport = async (courseId) => {
    if (!courseId) {
      throw new Error('Course ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.classDiagnosticReport(courseId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get analytics report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get eligible subjects with disciplines that have activities
   * @returns {Promise<Object>} The subjects data with nested disciplines
   */
  const getEligibleSubjects = async () => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.eligibleSubjects(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get eligible subjects: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get flagged messages for inappropriate use analytics
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {number} params.limit - Max messages to return
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<Object>} The flagged messages data with pagination
   */
  const getFlaggedMessages = async ({ courseIds, startDate, endDate, limit = 3, offset = 0, statuses, categories } = {}) => {
    try {
      const urls = getApiUrls();
      const params = new URLSearchParams();

      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&') : '';

      const categoryParams = categories?.length ? categories.map((cat) => `categories[]=${encodeURIComponent(cat)}`).join('&') : '';

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (statuses) params.append('statuses', statuses);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, categoryParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.flaggedMessages()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get flagged messages: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get available flagged message types (categories and statuses)
   * @returns {Promise<Object>} Object with categories and statuses arrays
   */
  const getFlaggedMessageTypes = async () => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.flaggedMessageTypes(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get flagged message types: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get topic detail (student breakdown) for a specific topic
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {string} params.topicId - The topic ID to get detail for
   * @returns {Promise<Object>} The topic detail data with user breakdown
   */
  const getTopicDetail = async ({ courseIds, startDate, endDate, topicId } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&') : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (topicId) params.append('topicId', topicId);

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.topicDetail()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get topic detail: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get topic ranking for analytics
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @param {number} params.limit - Max topics to return
   * @param {number} params.offset - Pagination offset
   * @returns {Promise<Object>} The topic ranking data
   */
  const getTopicRanking = async ({ courseIds, startDate, endDate, limit = 10, offset = 0 } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds[]=${encodeURIComponent(id)}`).join('&') : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.topicRanking()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get topic ranking: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Update the status of a flagged message annotation
   * @param {string} annotationId - The annotation ID to update
   * @param {Object} payload - The update payload
   * @param {string} payload.status - New status (e.g. 'REVIEWED', 'DISMISSED')
   * @param {string} [payload.notes] - Optional reviewer notes
   * @returns {Promise<Object>} The updated annotation
   */
  const updateAnnotationStatus = async (annotationId, { status, notes }) => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.annotationStatus(annotationId), {
        method: 'PATCH',
        headers: createHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ status, ...(notes !== undefined && { notes }) }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update annotation status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get intent distribution per student
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string[]} params.userIds - User IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @returns {Promise<Object>} The intent distribution data
   */
  const getIntentDistribution = async ({ courseIds, userIds, startDate, endDate } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds=${encodeURIComponent(id)}`).join('&') : '';
      const userIdParams = userIds?.length ? userIds.map((id) => `userIds=${encodeURIComponent(id)}`).join('&') : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, userIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.intentDistribution()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get intent distribution: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get tutor usage analytics (active users and interactions by mode)
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string} params.startDate - Start date ISO string
   * @param {string} params.endDate - End date ISO string
   * @returns {Promise<Object>} The use analytics data
   */
  const getUseAnalytics = async ({ courseIds, startDate, endDate } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds=${encodeURIComponent(id)}`).join('&') : '';

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.useAnalytics()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get use analytics: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get unified annotations (flagged messages + learning alerts)
   * @param {Object} params - Query parameters
   * @param {string[]} params.courseIds - Course IDs to filter by
   * @param {string[]} params.statuses - Annotation statuses to filter by
   * @param {number} params.limit - Max items to return (default 20, max 100)
   * @param {number} params.offset - Pagination offset (default 0)
   * @param {string} params.order - Sort order ('ASC' or 'DESC', default 'DESC')
   * @returns {Promise<Object>} The annotations data with pagination
   */
  const getAnnotations = async ({ courseIds, statuses, limit = 20, offset = 0, order = 'DESC' } = {}) => {
    try {
      const urls = getApiUrls();
      const courseIdParams = courseIds?.length ? courseIds.map((id) => `courseIds=${encodeURIComponent(id)}`).join('&') : '';
      const statusParams = statuses?.length ? statuses.map((s) => `statuses=${encodeURIComponent(s)}`).join('&') : '';

      const params = new URLSearchParams();
      params.append('limit', String(limit));
      params.append('offset', String(offset));
      params.append('order', order);

      const scalarQuery = params.toString();
      const queryString = [courseIdParams, statusParams, scalarQuery].filter(Boolean).join('&');

      const response = await fetch(`${urls.analytics.annotations()}?${queryString}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get annotations: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get annotation detail by ID
   * @param {string} annotationId - The annotation ID
   * @returns {Promise<Object>} The annotation detail data
   */
  const getAnnotationDetail = async (annotationId) => {
    if (!annotationId) {
      throw new Error('Annotation ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.annotationStatus(annotationId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get annotation detail: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get per-student KC progress detail
   * GET /stats/users/:userId/kcs/:kcId/progress
   * @param {string} userId - The student external ID
   * @param {string} kcId - The Knowledge Component ID
   * @returns {Promise<Object>} The KC progress detail item
   */
  const getUserKcProgress = async (userId, kcId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (!kcId) {
      throw new Error('KC ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.userKcProgress(userId, kcId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get user KC progress: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the flat list of activities responded by a student for a KC.
   * Sorted DESC by respondedAt (most recent first). Day grouping is done in the FE.
   * GET /stats/users/:userId/kcs/:kcId/activities
   * @param {string} userId - The student external ID
   * @param {string} kcId - The Knowledge Component ID
   * @returns {Promise<Object>} The KC activities response with an `activities` array
   */
  const getUserKcActivities = async (userId, kcId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (!kcId) {
      throw new Error('KC ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.userKcActivities(userId, kcId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get user KC activities: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the detail of a single activity instance, including the lemonade payload,
   * the student's response (if any), score and timing metadata.
   * GET /stats/activity-instances/:activityInstanceId
   * @param {string} activityInstanceId - The activity instance ID (UUID)
   * @returns {Promise<Object>} The activity instance detail
   */
  const getActivityInstanceDetail = async (activityInstanceId) => {
    if (!activityInstanceId) {
      throw new Error('Activity instance ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.activityInstance(activityInstanceId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const error = new Error(`Failed to get activity instance detail: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the list of programs (courses) shared between the authenticated teacher and a student.
   * Returns only programs that have at least one KC configured and at least one ActivitySession for the student.
   * GET /stats/users/:userId/courses
   * @param {string} userId - The student external ID
   * @returns {Promise<{ items: Array<{ courseId: string, title: string, blockedCount: number }> }>} The shared programs response
   */
  const getStudentSharedPrograms = async (userId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.userCourses(userId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get student shared programs: ${response.status}`);
      }

      const data = await response.json();
      // blockedCount drives the per-tab badge; coerce to a finite number (defaults to 0) so the
      // UI never has to guard against a missing/NaN value if the backend omits it.
      const items = Array.isArray(data?.items)
        ? data.items.map((item) => ({
            courseId: item.courseId,
            title: item.title,
            blockedCount: Number.isFinite(item.blockedCount) ? item.blockedCount : 0,
          }))
        : [];
      return { items };
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the practice sessions of a student in a course within a date range.
   * The date filter applies per performed activity (performedAt), not per session createdAt.
   * GET /stats/users/:userId/courses/:courseId/sessions?startDate=...&endDate=...
   * @param {string} userId - The student external ID
   * @param {string} courseId - The course ID
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date ISO string (required)
   * @param {string} params.endDate - End date ISO string (required)
   * @returns {Promise<{ sessions: Array }>} The student sessions response
   */
  const getUserCourseSessions = async (userId, courseId, { startDate, endDate } = {}) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (!courseId) {
      throw new Error('Course ID is required');
    }

    try {
      const urls = getApiUrls();

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();

      const response = await fetch(`${urls.analytics.userCourseSessions(userId, courseId)}${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const error = new Error(`Failed to get user course sessions: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Mark annotations as viewed (read)
   * @param {string[]} annotationIds - Array of annotation IDs to mark as viewed
   * @returns {Promise<Object>} The response data
   */
  const markAnnotationsAsViewed = async (annotationIds) => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.annotationViews(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ annotationIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to mark annotations as viewed: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the table of contents (units → lessons → KCs) of a single course for one student,
   * with the student's mastery/progress over each KC.
   * @param {string} userId - The student external ID
   * @param {string} courseId - The course ID of the active tab
   * @returns {Promise<{ courseId: string, title: string, units: Array<object> }>} The course ToC with progress
   */
  const getUserCourseToc = async (userId, courseId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    if (!courseId) {
      throw new Error('Course ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.userCourseToc(userId, courseId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get user course ToC: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the course ToC with progress for the LOGGED-IN student (user resolved from
   * the session token, no userId in the path). Same shape as `getUserCourseToc`.
   * GET /stats/user/courses/:courseId/toc
   * @param {string} courseId - The course ID
   * @returns {Promise<{ courseId: string, title: string, units: Array<object> }>}
   */
  const getSessionCourseToc = async (courseId) => {
    if (!courseId) {
      throw new Error('Course ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.sessionCourseToc(courseId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get session course ToC: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the list of students grouped by course for the authenticated teacher.
   * GET /stats/teacher/students-by-courses?courseIds=<id>&courseIds=<id>
   * @param {string[]} courseIds - Course IDs to query (required, non-empty)
   * @returns {Promise<{ courses: Array<{ courseId: string, students: Array<{ id: string, externalId: string, name: string, lastName: string, usageQualityScore: number | null }> }> }>}
   */
  const getTeacherStudentsByCourses = async (courseIds) => {
    if (!Array.isArray(courseIds) || courseIds.length === 0) {
      throw new Error('courseIds is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.analytics.teacherStudentsByCourses(courseIds), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        const error = new Error(`Failed to get teacher students by courses: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  // Return the service API
  return {
    getActivityInstanceDetail,
    getAnnotationDetail,
    getAnnotations,
    getClassDiagnosticReport,
    getEligibleSubjects,
    getFlaggedMessages,
    getFlaggedMessageTypes,
    getIntentDistribution,
    getStudentSharedPrograms,
    getTeacherStudentsByCourses,
    getTopicDetail,
    getTopicRanking,
    getUseAnalytics,
    getUserCourseSessions,
    getUserCourseToc,
    getSessionCourseToc,
    getUserKcActivities,
    getUserKcProgress,
    markAnnotationsAsViewed,
    updateAnnotationStatus,
  };
}

// Default export with default configuration
export default createAnalyticsService();
